import { useState, useEffect, Component } from 'react';
import { Player } from '@remotion/player';
import { transform } from 'sucrase';
import * as Remotion from 'remotion';
import * as RemotionTransitions from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import React from 'react';

/**
 * Error Boundary for thumbnail rendering
 */
interface ErrorBoundaryProps {
  children: React.ReactNode;
  onError: () => void;
  fallback: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ThumbnailErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export interface VideoThumbnailProps {
  code: string;
  componentName: string;
  preferences: {
    fps: number;
    width: number;
    height: number;
    durationInSeconds: number;
  };
  className?: string;
}

interface ThumbnailState {
  component: React.ComponentType | null;
  error: boolean;
}

/**
 * Strip import statements and prepare code for execution
 */
function prepareCode(code: string): string {
  let prepared = code
    .replace(/^import\s*\{[\s\S]*?\}\s*from\s*['"][^'"]+['"];?\s*$/gm, '')
    .replace(/^import\s+.*?from\s*['"][^'"]+['"];?\s*$/gm, '')
    .replace(/^import\s*['"][^'"]+['"];?\s*$/gm, '')
    .replace(/^import\s+type\s+.*?from\s*['"][^'"]+['"];?\s*$/gm, '');

  prepared = prepared.replace(/export\s+default\s+/g, '');
  prepared = prepared.replace(/export\s+const\s+/g, 'const __export__ ');
  prepared = prepared.replace(/export\s+function\s+/g, 'function __export__ ');

  return prepared;
}

/**
 * Extract exported component names from prepared code
 */
function extractExportedNames(code: string): string[] {
  const names: string[] = [];
  const constMatch = code.matchAll(/const\s+__export__\s+(\w+)/g);
  const funcMatch = code.matchAll(/function\s+__export__\s+(\w+)/g);

  for (const match of constMatch) {
    names.push(match[1]);
  }
  for (const match of funcMatch) {
    names.push(match[1]);
  }

  return names;
}

/**
 * Error placeholder component
 */
function ErrorPlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-100">
      <svg
        className="h-8 w-8 text-slate-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
        />
      </svg>
    </div>
  );
}

/**
 * Loading placeholder component
 */
function LoadingPlaceholder() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-slate-100">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-500" />
    </div>
  );
}

/**
 * Lightweight video thumbnail component for gallery display.
 * Shows the first frame only, no controls or autoplay.
 */
export function VideoThumbnail({
  code,
  componentName,
  preferences,
  className = '',
}: VideoThumbnailProps) {
  const [state, setState] = useState<ThumbnailState>({
    component: null,
    error: false,
  });

  const { fps, width, height, durationInSeconds } = preferences;
  const durationInFrames = fps * durationInSeconds;

  useEffect(() => {
    if (!code) {
      setState({ component: null, error: false });
      return;
    }

    try {
      const prepared = prepareCode(code);
      const exportedNames = extractExportedNames(prepared);

      const cleanCode = prepared
        .replace(/const\s+__export__\s+/g, 'const ')
        .replace(/function\s+__export__\s+/g, 'function ');

      const transpiled = transform(cleanCode, {
        transforms: ['typescript', 'jsx'],
        jsxRuntime: 'classic',
        production: true,
        disableESTransforms: true,
      }).code;

      const context: Record<string, unknown> = {
        React,
        useState: React.useState,
        useEffect: React.useEffect,
        useMemo: React.useMemo,
        useCallback: React.useCallback,
        useRef: React.useRef,
        ...Remotion,
        ...RemotionTransitions,
        fade,
        slide,
        wipe,
      };

      const captureExports = exportedNames.length > 0
        ? `\ncontext.__exports = {};\n${exportedNames.map(name =>
            `if (typeof ${name} !== 'undefined') context.__exports.${name} = ${name};`
          ).join('\n')}`
        : '';

      const wrappedCode = `
        const {
          React, useState, useEffect, useMemo, useCallback, useRef,
          useCurrentFrame, useVideoConfig, interpolate, spring,
          Sequence, Series, AbsoluteFill, Easing, Composition,
          staticFile, continueRender, delayRender, random,
          Loop, Audio, Video, Img, OffthreadVideo,
          getInputProps, measureSpring, interpolateColors,
          TransitionSeries, linearTiming, springTiming,
          fade, slide, wipe
        } = context;

        ${transpiled}
        ${captureExports}
      `;

      const fn = new Function('context', wrappedCode);
      fn(context);

      const exports = (context.__exports || {}) as Record<string, unknown>;
      const Component = exports[componentName] ||
        Object.values(exports).find((exp) => typeof exp === 'function');

      if (!Component || typeof Component !== 'function') {
        throw new Error('Component not found');
      }

      setState({ component: Component as React.ComponentType, error: false });
    } catch {
      setState({ component: null, error: true });
    }
  }, [code, componentName]);

  const containerClasses = `relative aspect-video overflow-hidden rounded-lg ${className}`.trim();

  if (state.error) {
    return (
      <div className={containerClasses}>
        <ErrorPlaceholder />
      </div>
    );
  }

  if (!state.component) {
    return (
      <div className={containerClasses}>
        <LoadingPlaceholder />
      </div>
    );
  }

  const codeKey = code ? code.slice(0, 100) + code.length : 'empty';

  return (
    <div className={containerClasses}>
      <ThumbnailErrorBoundary
        key={codeKey}
        onError={() => setState({ component: null, error: true })}
        fallback={<ErrorPlaceholder />}
      >
        <Player
          component={state.component}
          durationInFrames={durationInFrames}
          fps={fps}
          compositionWidth={width}
          compositionHeight={height}
          style={{ width: '100%', height: '100%' }}
        />
      </ThumbnailErrorBoundary>
    </div>
  );
}
